import { isMcFaroProdCookieEnabled } from './faro-prod-cookie';

export type FaroTier = 'dev' | 'qa' | 'stg' | 'prod' | 'off';

export type FaroProduct =
  | 'mimecast-ui'
  | 'hrm'
  | 'universal-login'
  | 'app-launcher'
  | 'safe-send'
  | 'user-portal';

type FaroTierMap = Record<Exclude<FaroTier, 'off'>, string>;

function tierMap(
  dev: string,
  qa: string,
  stg: string,
  prod: string
): FaroTierMap {
  return { dev, qa, stg, prod };
}

function appNamesFor(prefix: string): FaroTierMap {
  return tierMap(
    `${prefix}-dev`,
    `${prefix}-qa`,
    `${prefix}-stg`,
    `${prefix}-prod`
  );
}

const COLLECTOR_IDS: Record<FaroProduct, FaroTierMap> = {
  'mimecast-ui': {
    dev: '82cb3c0c8687588011e87895e454a908',
    qa: 'a30c5c82e9d0ed448d5cd5d6144bcabe',
    stg: 'd119057c7f9f95c499738fb3c6c4bb9a',
    prod: '3c66fa60d885fbb18e936bbf7f295acd',
  },
  hrm: {
    dev: '7e4fc327a762aec7a81adcde978b559a',
    qa: 'ab597e70b304739f0cb1abef59cd3058',
    stg: '6607d6e3ca6cf6bec452a9d07817f634',
    prod: 'd5b41663a28867edfdef9889ad81713f',
  },
  'universal-login': {
    dev: 'fa6b8a67435df12d9801fe53c3bca5a7',
    qa: 'caf4a8ca9ecaa405607660180c2439f8',
    stg: '41147ee8f320069144bd55d5cf4acc07',
    prod: 'e231ecaefeca002159eb0aac2873ddb1',
  },
  'app-launcher': {
    dev: '4dd00a45dbc97feec0c48cb74ebb3710',
    qa: '5fddca20e578e3d404832986e7870329',
    stg: '5f3a6909d41730b11693369ba29a3f7e',
    prod: '91006f01939611ec0045a806273665fa',
  },
  'safe-send': {
    dev: '3210b22e350f554d6185582af9d7ced0',
    qa: '4bedcd3e5c07f00f0d284267277f60a5',
    stg: '1d3d95916937fa40cfba4ac6ce83f17f',
    prod: 'd8ba1f1875b77e0b246a0642929beea0',
  },
  'user-portal': {
    dev: '65415051e7d149b741b33105da920a44',
    qa: '26d629beb8b079aef2818adf278ed1b8',
    stg: '05b0f5e6ce0b7f68acdb32f039e3c23d',
    prod: 'c9bc24338a14b94f6b8f6705d4c68403',
  },
};

const APP_NAMES: Record<FaroProduct, FaroTierMap> = {
  'mimecast-ui': appNamesFor('fep-mimecast-ui'),
  hrm: appNamesFor('fep-hrm'),
  'universal-login': appNamesFor('fep-mul'),
  'app-launcher': appNamesFor('fep-app-launcher'),
  'safe-send': appNamesFor('eua-safesend'),
  'user-portal': appNamesFor('eua-eup'),
};

/** Matches grafana-stack `deployment_environment_name` in frontend-o11y.tf */
const FARO_ENV_BY_TIER: Record<Exclude<FaroTier, 'off'>, string> = {
  dev: 'dev',
  qa: 'qa',
  stg: 'stg',
  prod: 'prod',
};

export interface FaroRuntimeConfig {
  enabled: boolean;
  collectorUrl: string;
  appName: string;
  faroEnvironment: string;
  resourceAttributes: Record<string, string>;
}

export function resolveFaroTier(hostname: string): FaroTier {
  const h = hostname.toLowerCase();

  if (h.includes('login-stg.') && h.includes('mimecast-qa.com')) {
    return 'stg';
  }
  if (h.includes('mimecast-dev.com')) {
    return 'dev';
  }
  if (h.includes('mimecast-qa.com')) {
    return 'qa';
  }
  if (
    h === 'mimecast.com' ||
    h.endsWith('.mimecast.com') ||
    h.endsWith('.mimecast-offshore.com')
  ) {
    return 'prod';
  }
  return 'off';
}

export function gridFromHostname(hostname: string): string {
  const tier = resolveFaroTier(hostname);
  const h = hostname.toLowerCase();
  if (tier === 'off') {
    return '';
  }
  if (tier !== 'prod') {
    return tier;
  }
  const gridRegex = new RegExp(/login-(\w+).mimecast(?:-\w+)?.com/g);
  const match = gridRegex.exec(h);
  if (!match) {
    return '';
  }
  if (match[1] === 'je') {
    return 'jer';
  }
  return match[1];
}

export interface FaroRuntimeOptions {
  gridAttributeKey?: string;
}

export function getFaroRuntimeConfig(
  product: FaroProduct,
  hostname: string,
  options?: FaroRuntimeOptions
): FaroRuntimeConfig {
  const tier = resolveFaroTier(hostname);
  const grid = gridFromHostname(hostname);
  const resourceCluster = `${tier}-grid-${grid}-fep`;

  if (tier === 'off') {
    return {
      enabled: false,
      collectorUrl: '',
      appName: '',
      faroEnvironment: 'dev',
      resourceAttributes: {},
    };
  }
  const enabled = tier !== 'prod' || isMcFaroProdCookieEnabled();
  const id = COLLECTOR_IDS[product][tier];

  const gridKey = options?.gridAttributeKey ?? 'region';

  return {
    enabled,
    collectorUrl: `/collector/${id}`,
    appName: APP_NAMES[product][tier],
    faroEnvironment: FARO_ENV_BY_TIER[tier],
    resourceAttributes: grid
      ? {
          [gridKey]: grid,
          cluster: resourceCluster,
        }
      : {},
  };
}
